import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewOperationDate } from 'utils/getNewOperationDate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';

import { HistoryMovement, Movement, SelectorOption, Transaction } from 'types';

import { cleanValue } from 'constants/formats';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import transactionTypes from 'constants/transactionTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { useGetAvailableRangeByHoldingClass } from 'modules/_shared/hooks/useGetAvailableRangeByHoldingClass';
import { createTransaction } from 'modules/transactions/utils/createTransaction';
import movementTypes from 'modules/transactions/constants/movementTypes';

import { createMovement } from 'modules/transactions/utils/createMovement';
import { ConstitutionModalContextType } from '../types/ConstitutionModalContextType';
import { constitutionModalInitValue } from '../constants/constitutionModalInitValue';

const ConstitutionModalContext = createContext<ConstitutionModalContextType>(
  constitutionModalInitValue
);

export const ConstitutionModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>('');
  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [totalSharesToAdd, setTotalSharesToAdd] = useState<number>(0);
  const [nominalValue, setNominalValue] = useState<number>(0);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [sharesToAdd, setSharesToAdd] = useState<number>(0);
  const [remainingShares, setRemainingShares] = useState<number>(0);
  const [hasPremium, setHasPremium] = useState<boolean>(false);
  const [premium, setPremium] = useState<number>(0);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isValidateRangeEnabled, setIsValidateRangeEnabled] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { lastOperationDate } = useGetLastOperationDate(
    actualSociety?._id
  ).data;

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      isSingleSelect: true,
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: actualSociety?._id,
    });

  const {
    data: availableRangeData,
    actions: availableRangeActions,
    isLoading: isLoadingRange,
  } = useGetAvailableRangeByHoldingClass();

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const handleChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const handleChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const handleChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeTotalSharesToAdd = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setTotalSharesToAdd(cleanedValue);
    setRemainingShares(cleanedValue);
  };

  const handleChangeNominalValue = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setNominalValue(cleanedValue);
  };

  const handleChangePartner = (selectedOption: SelectorOption) => {
    setSelectedPartner(selectedOption?.value || '');
    partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
      selectedOption
    );
  };

  const handleChangeSharesToAdd = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setSharesToAdd(cleanedValue);
    setIsValidateRangeEnabled(true);
  };

  const handleChangeHasPremium = () => {
    setHasPremium((prev) => !prev);
    setPremium(0);
  };

  const handleChangePremium = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setPremium(cleanedValue);
  };

  const handleSaveMovement = () => {
    const range = {
      ...availableRangeData.availableRange,
      shareClass: holdingClassOptionsData.selectedHoldingClass,
      sharePremium: premium,
      partner: selectedPartner,
    };
    const movement = createMovement({
      range,
      movementType: movementTypes.INCREMENT,
    });
    const transaction = createTransaction({
      range,
      transactionType: transactionTypes.CONSTITUTION,
    });

    setMovements([...movements, movement]);
    setTransactions([...transactions, transaction]);

    const historyMovement: HistoryMovement = {
      from: range.from,
      to: range.to,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      partnerNameSeller: partnerOptionsData.selectedPartnerFirstOption?.label
        .split('|')[1]
        .trim(),
    };

    setHistoryMovements([...historyMovements, historyMovement]);

    availableRangeActions.handleAddRange({
      from: range.from,
      to: range.to,
    });

    setSharesToAdd(0);
    setRemainingShares((prev) => prev - sharesToAdd);
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    holdingClassOptionsActions.handleClassOptionChange(selectedOption);
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: selectedOption?.value || '',
      sharesCount: sharesToAdd,
    });
  };

  const handleValidateRange = () => {
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharesCount: sharesToAdd,
    });
  };

  const handleSaveConstitution = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: operationTypes.CONTRIBUTION_FILENAME,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const operationData = {
          movements,
          transactions,
          date: newOperationDate,
          nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          operationType: operationTypes.CONSTITUTION,
        };

        await store.dispatch(addOperation(operationData));
        setIsLoading(false);

        successSwal(t('ConstitutionAddedSuccessfully'));
      } catch (error) {
        errorSwal(t('ErrorAddingConstitution'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <ConstitutionModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          lastOperationDate,
          totalSharesToAdd,
          nominalValue,
          selectedPartner,
          partnerOptions: partnerOptionsData.partnerFirstOptions,
          sharesToAdd,
          remainingShares,
          hasPremium,
          premium,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          defaultHoldingClassOption:
            holdingClassOptionsData.defaultHoldingClassOption,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          availableRange: availableRangeData.availableRange,
          historyMovements,
          isValidateRangeEnabled,
          isLoadingRange,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          handleChangeNotaryName,
          handleChangeNotaryProtocol,
          handleChangeNotaryCity,
          handleChangeTotalSharesToAdd,
          handleChangeNominalValue,
          handleChangePartner,
          handleSearchPartnerByTerm: partnerOptionsActions.fetchPartnersByTerm,
          handleChangeSharesToAdd,
          handleChangeHasPremium,
          handleChangePremium,
          handleChangeHoldingClass,
          handleValidateRange,
          handleSaveMovement,
          handleSaveConstitution,
        },
      }}
    >
      {children}
    </ConstitutionModalContext.Provider>
  );
};

export const useConstitutionModal = () => {
  const context = useContext(ConstitutionModalContext);
  if (!context) {
    throw new Error(
      'useConstitutionModal must be used within a ConstitutionModalProvider'
    );
  }
  return context;
};
